import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    User,
    Users,
    Mail,
    Phone,
    Calendar,
    GraduationCap,
    MapPin,
    BadgeCheck,
    Camera,
    IdCard,
} from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import normalizePhoneNumber from "../utils/normalizePhoneNumber";
import { decryptToken } from "../utils/getToken";
import AdmissionCenterSelect from "./AdmissionCenterSelect";
import { safeResponseJson, normalizeAdmissionBatchIdForApi } from "../../utils/admissionCenters";
import { admissionAssetUrl, resolveAdmissionDocumentsForSave, isPdfPath } from "./admissionFormUpload";
import "./AddAdmission.css";

const AddAdmission2 = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const token = decryptToken("accessToken") || "";
    const submittedData = location.state || null;
    const url = API_BASE_URL;
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const isEdit = Boolean(submittedData?.id);

    const [formData, setFormData] = useState({
        id: "",
        name: "",
        fatherName: "",
        motherName: "",
        dob: "",
        gender: "",
        school: "",
        address: "",
        pincode: "",
        mobile: "",
        email: "",
        center: "",
        main_id: isLoggedIn?.main_id || "",
        status: "",
        studentImage: "",
        aadharImage: "",
        batch_id: "",
    });

    const [batchesList, setBatchesList] = useState([]);

    const [studentImageFile, setStudentImageFile] = useState(null);
    const [aadharImageFile, setAadharImageFile] = useState(null);
    const [studentPreview, setStudentPreview] = useState(null);
    const [aadharPreview, setAadharPreview] = useState(null);
    const studentFileRef = useRef(null);
    const aadharFileRef = useRef(null);

    const formatToInputDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    };

    useEffect(() => {
        if (!studentImageFile) {
            setStudentPreview(null);
            return undefined;
        }
        const u = URL.createObjectURL(studentImageFile);
        setStudentPreview(u);
        return () => URL.revokeObjectURL(u);
    }, [studentImageFile]);

    useEffect(() => {
        if (!aadharImageFile || !aadharImageFile.type.startsWith("image/")) {
            setAadharPreview(null);
            return undefined;
        }
        const u = URL.createObjectURL(aadharImageFile);
        setAadharPreview(u);
        return () => URL.revokeObjectURL(u);
    }, [aadharImageFile]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "mobile") {
            setFormData((prev) => ({
                ...prev,
                mobile: normalizePhoneNumber(value),
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const editSourceId = submittedData?.id != null ? String(submittedData.id) : "";
    const editNavKey = location.key;

    useEffect(() => {
        if (!submittedData) return;
        const s = submittedData;
        setFormData({
            id: s.id || "",
            name: s.name || "",
            fatherName: s.fatherName || "",
            motherName: s.motherName || "",
            dob: formatToInputDate(s.dob),
            gender: s.gender || "",
            school: s.school || "",
            address: s.address || "",
            pincode: s.pincode || "",
            mobile: s.mobile || "",
            email: s.email || "",
            center: s.center || "",
            main_id: isLoggedIn?.main_id || "",
            status: s.status || "",
            studentImage: s.studentImage || "",
            aadharImage: s.aadharImage || "",
            batch_id: s.batch_id != null && s.batch_id !== "" ? String(s.batch_id) : "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editNavKey, editSourceId]);

    useEffect(() => {
        const mid = isLoggedIn?.main_id;
        if (mid == null || mid === "") return;
        setFormData((prev) => (prev.main_id === mid ? prev : { ...prev, main_id: mid }));
    }, [isLoggedIn?.main_id]);

    useEffect(() => {
        if (!formData.center || !token) {
            setBatchesList([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${url}/admission/batches/for-admission?center=${encodeURIComponent(formData.center)}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                const data = await safeResponseJson(res);
                if (cancelled) return;
                if (!res.ok) {
                    setBatchesList([]);
                    return;
                }
                setBatchesList(Array.isArray(data.batches) ? data.batches : []);
            } catch {
                if (!cancelled) setBatchesList([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [formData.center, token, url]);

    const onPickStudentImage = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            alert("Please choose an image file for the student photo.");
            return;
        }
        setStudentImageFile(f);
    };

    const onPickAadharDoc = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        const ok = f.type.startsWith("image/") || f.type === "application/pdf";
        if (!ok) {
            alert("Please choose an image or PDF for the Aadhaar document.");
            return;
        }
        setAadharImageFile(f);
    };

    const clearStudentImage = () => {
        setStudentImageFile(null);
        setFormData((prev) => ({ ...prev, studentImage: "" }));
    };

    const clearAadharImage = () => {
        setAadharImageFile(null);
        setFormData((prev) => ({ ...prev, aadharImage: "" }));
    };

    const storedStudentUrl =
        !studentPreview && formData.studentImage && !isPdfPath(formData.studentImage)
            ? admissionAssetUrl(url, formData.studentImage)
            : null;
    const storedAadharUrl = formData.aadharImage ? admissionAssetUrl(url, formData.aadharImage) : null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !formData.id &&
            formData.center &&
            batchesList.length > 0
        ) {
            const hasBatch =
                formData.batch_id !== "" &&
                formData.batch_id != null &&
                !Number.isNaN(parseInt(formData.batch_id, 10));
            if (!hasBatch) {
                alert(
                    "This center has batches defined. Select a batch for this student so they appear in the batch student count."
                );
                return;
            }
        }

        try {
            let studentImage = formData.studentImage || null;
            let aadharImage = formData.aadharImage || null;

            const docExtra = await resolveAdmissionDocumentsForSave(url, token, {
                studentFile: studentImageFile,
                aadharFile: aadharImageFile,
            });
            if (docExtra.studentImage) studentImage = docExtra.studentImage;
            if (docExtra.aadharImage) aadharImage = docExtra.aadharImage;

            if (!studentImage) studentImage = null;
            if (!aadharImage) aadharImage = null;

            const method = formData.id ? "PUT" : "POST";
            const apiUrl = formData.id ? `${url}/admission/${formData.id}` : `${url}/admission/admin`;

            const body = {
                id: formData.id,
                name: formData.name,
                fatherName: formData.fatherName,
                motherName: formData.motherName,
                dob: formData.dob,
                gender: formData.gender,
                school: formData.school,
                address: formData.address,
                pincode: formData.pincode,
                mobile: formData.mobile,
                email: formData.email,
                center: formData.center,
                main_id: formData.main_id,
                status: formData.status,
                batch_id: normalizeAdmissionBatchIdForApi(formData.batch_id),
                studentImage,
                aadharImage,
            };
            if (docExtra.studentImageDataUrl) body.studentImageDataUrl = docExtra.studentImageDataUrl;
            if (docExtra.aadharImageDataUrl) body.aadharImageDataUrl = docExtra.aadharImageDataUrl;

            const payload = formData.id
                ? body
                : {
                      ...body,
                      main_id: isLoggedIn?.main_id,
                      user_name: isLoggedIn?.full_name || isLoggedIn?.email || "Admin",
                      rulesAgreed: "1",
                  };

            const res = await fetch(apiUrl, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await res.json();

            if (res.ok) {
                alert(`Admission ${formData.id ? "updated" : "created"} successfully`);
                navigate("/admission");
            } else {
                alert(result.message || result.error || "Submission failed");
            }
        } catch (err) {
            console.error(err);
            alert(err.message || "Server error");
        }
    };

    return (
        <div className="admission-form-shell">
            <div className="admission-form-shell__bg" aria-hidden />
            <div className="admission-form-container">
                <header className="admission-form-header">
                    <button
                        type="button"
                        className="admission-form-back"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Back
                    </button>
                    <div className="admission-form-header-text">
                        <h1>{isEdit ? "Edit admission" : "New admission"}</h1>
                        <p>
                            {isEdit
                                ? "Update student details and enrollment. Changes apply immediately after save."
                                : "Capture student, contact, and center details. Fields marked with * are required. Photo and Aadhaar document are optional."}
                        </p>
                    </div>
                </header>

                <div className="admission-form-card">
                    <form className="admission-form-premium" onSubmit={handleSubmit}>
                        <section className="admission-form-section" aria-labelledby="af-section-student">
                            <h2 id="af-section-student" className="admission-form-section__title">
                                <User size={16} strokeWidth={2.25} aria-hidden />
                                Student details
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field admission-form-field--full">
                                    <div className="admission-file-upload">
                                        <span className="admission-file-upload__label" id="af-student-photo-label">
                                            <Camera
                                                size={14}
                                                style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                                aria-hidden
                                            />
                                            Student photo <span className="admission-file-upload__hint">(optional)</span>
                                        </span>
                                        <p className="admission-file-upload__hint">JPG, PNG, or WebP — max 5 MB.</p>
                                        <input
                                            ref={studentFileRef}
                                            id="af-student-file"
                                            className="admission-file-upload__visually-hidden"
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/jpg"
                                            aria-labelledby="af-student-photo-label"
                                            onChange={onPickStudentImage}
                                        />
                                        <label htmlFor="af-student-file" className="admission-file-upload__zone admission-file-upload__zone--photo">
                                            {studentPreview || storedStudentUrl ? (
                                                <img
                                                    className="admission-file-upload__preview admission-file-upload__preview--avatar"
                                                    src={studentPreview || storedStudentUrl}
                                                    alt=""
                                                />
                                            ) : (
                                                <span className="admission-file-upload__hint">
                                                    Click to upload a photo
                                                </span>
                                            )}
                                        </label>
                                        <div className="admission-file-upload__actions">
                                            <button
                                                type="button"
                                                className="admission-file-upload__btn"
                                                onClick={() => studentFileRef.current?.click()}
                                            >
                                                Choose file
                                            </button>
                                            {(studentImageFile || formData.studentImage) && (
                                                <button
                                                    type="button"
                                                    className="admission-file-upload__btn admission-file-upload__btn--danger"
                                                    onClick={clearStudentImage}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-name">
                                        Full name<span className="req">*</span>
                                    </label>
                                    <input
                                        id="af-name"
                                        className="admission-form-input"
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Student name"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-father">
                                        Father&apos;s name
                                    </label>
                                    <input
                                        id="af-father"
                                        className="admission-form-input"
                                        type="text"
                                        name="fatherName"
                                        value={formData.fatherName}
                                        onChange={handleInputChange}
                                        placeholder="Father name"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-mother">
                                        Mother&apos;s name
                                    </label>
                                    <input
                                        id="af-mother"
                                        className="admission-form-input"
                                        type="text"
                                        name="motherName"
                                        value={formData.motherName}
                                        onChange={handleInputChange}
                                        placeholder="Mother name"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-dob">
                                        <Calendar
                                            size={14}
                                            style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                            aria-hidden
                                        />
                                        Date of birth
                                    </label>
                                    <input
                                        id="af-dob"
                                        className="admission-form-input"
                                        type="date"
                                        name="dob"
                                        value={formData.dob}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-gender">
                                        Gender
                                    </label>
                                    <select
                                        id="af-gender"
                                        className="admission-form-select"
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Transgender">Transgender</option>
                                    </select>
                                </div>

                                <div className="admission-form-field admission-form-field--full">
                                    <div className="admission-file-upload">
                                        <span className="admission-file-upload__label" id="af-aadhar-doc-label">
                                            <IdCard
                                                size={14}
                                                style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                                aria-hidden
                                            />
                                            Aadhaar document <span className="admission-file-upload__hint">(optional)</span>
                                        </span>
                                        <p className="admission-file-upload__hint">
                                            Upload a clear photo or PDF of the Aadhaar card — max 5 MB.
                                        </p>
                                        <input
                                            ref={aadharFileRef}
                                            id="af-aadhar-file"
                                            className="admission-file-upload__visually-hidden"
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/jpg,application/pdf"
                                            aria-labelledby="af-aadhar-doc-label"
                                            onChange={onPickAadharDoc}
                                        />
                                        {formData.aadharImage && isPdfPath(formData.aadharImage) && !aadharImageFile ? (
                                            <div className="admission-file-upload__zone">
                                                <a
                                                    className="admission-file-upload__link"
                                                    href={storedAadharUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View current PDF
                                                </a>
                                            </div>
                                        ) : (
                                            <label htmlFor="af-aadhar-file" className="admission-file-upload__zone">
                                                {aadharImageFile ? (
                                                    aadharPreview ? (
                                                        <img
                                                            className="admission-file-upload__preview"
                                                            src={aadharPreview}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <span className="admission-file-upload__filename">{aadharImageFile.name}</span>
                                                    )
                                                ) : formData.aadharImage ? (
                                                    <img
                                                        className="admission-file-upload__preview"
                                                        src={storedAadharUrl}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <span className="admission-file-upload__hint">
                                                        Click to upload image or PDF
                                                    </span>
                                                )}
                                            </label>
                                        )}
                                        <div className="admission-file-upload__actions">
                                            <button
                                                type="button"
                                                className="admission-file-upload__btn"
                                                onClick={() => aadharFileRef.current?.click()}
                                            >
                                                Choose file
                                            </button>
                                            {(aadharImageFile || formData.aadharImage) && (
                                                <button
                                                    type="button"
                                                    className="admission-file-upload__btn admission-file-upload__btn--danger"
                                                    onClick={clearAadharImage}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="af-school">
                                        <GraduationCap
                                            size={14}
                                            style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                            aria-hidden
                                        />
                                        School
                                    </label>
                                    <input
                                        id="af-school"
                                        className="admission-form-input"
                                        type="text"
                                        name="school"
                                        value={formData.school}
                                        onChange={handleInputChange}
                                        placeholder="School name"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="af-section-contact">
                            <h2 id="af-section-contact" className="admission-form-section__title">
                                <Users size={16} strokeWidth={2.25} aria-hidden />
                                Contact
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-email">
                                        <Mail size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} aria-hidden />
                                        Email
                                    </label>
                                    <input
                                        id="af-email"
                                        className="admission-form-input"
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="name@example.com"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-mobile">
                                        <Phone size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} aria-hidden />
                                        Phone
                                    </label>
                                    <input
                                        id="af-mobile"
                                        className="admission-form-input"
                                        type="tel"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        maxLength={10}
                                        placeholder="10-digit mobile"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="af-section-address">
                            <h2 id="af-section-address" className="admission-form-section__title">
                                <MapPin size={16} strokeWidth={2.25} aria-hidden />
                                Address
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="af-address">
                                        Street address
                                    </label>
                                    <input
                                        id="af-address"
                                        className="admission-form-input"
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        placeholder="House, street, area"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-pincode">
                                        Pincode
                                    </label>
                                    <input
                                        id="af-pincode"
                                        className="admission-form-input"
                                        type="text"
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={handleInputChange}
                                        placeholder="PIN"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="af-section-enrollment">
                            <h2 id="af-section-enrollment" className="admission-form-section__title">
                                <BadgeCheck size={16} strokeWidth={2.25} aria-hidden />
                                Center &amp; batch
                            </h2>
                            <p className="admission-file-upload__hint" style={{ marginBottom: 12 }}>
                                Choose the center first, then a batch for this student. Schedule and teacher come from the batch record only.
                            </p>
                            <div className="admission-form-grid">
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-center">
                                        Center
                                    </label>
                                    <AdmissionCenterSelect
                                        name="center"
                                        value={formData.center}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                center: e.target.value,
                                                batch_id: "",
                                            }))
                                        }
                                        token={token}
                                        className="admission-form-select"
                                    />
                                </div>
                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="af-batch">
                                        Batch
                                    </label>
                                    <select
                                        id="af-batch"
                                        className="admission-form-select"
                                        name="batch_id"
                                        value={formData.batch_id}
                                        onChange={handleInputChange}
                                        disabled={!formData.center}
                                    >
                                        <option value="">
                                            {formData.center
                                                ? batchesList.length > 0
                                                    ? "Select batch (required)"
                                                    : "No batches for this center — create one under Batches"
                                                : "Select a center first"}
                                        </option>
                                        {formData.batch_id &&
                                            !batchesList.some(
                                                (b) => String(b.id) === String(formData.batch_id)
                                            ) && (
                                                <option value={formData.batch_id}>
                                                    Saved batch #{formData.batch_id} (change center to reload list)
                                                </option>
                                            )}
                                        {batchesList.map((b) => (
                                            <option key={b.id} value={String(b.id)}>
                                                {b.scheduleSummary || b.name}
                                                {typeof b.studentCount === "number" && b.max_students
                                                    ? ` · ${b.studentCount}/${b.max_students}`
                                                    : ""}
                                            </option>
                                        ))}
                                    </select>
                                    {(() => {
                                        const sel = batchesList.find(
                                            (b) => String(b.id) === String(formData.batch_id)
                                        );
                                        return sel ? (
                                            <p className="admission-file-upload__hint" style={{ marginTop: 8 }}>
                                                {sel.scheduleSummary}
                                            </p>
                                        ) : null;
                                    })()}
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-status">
                                        Status
                                    </label>
                                    <select
                                        id="af-status"
                                        className="admission-form-select"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="Active">Active</option>
                                        <option value="Deactive">Deactive</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Failed">Failed</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        <footer className="admission-form-footer">
                            <button
                                type="button"
                                className="admission-form-btn admission-form-btn--secondary"
                                onClick={() => navigate("/admission")}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="admission-form-btn admission-form-btn--primary">
                                {formData.id ? "Save changes" : "Create admission"}
                            </button>
                        </footer>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddAdmission2;
